import React, { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next'
import { useFormContext, Controller } from 'react-hook-form'
import { format } from 'date-fns';
import pwdgen from 'generate-password';
import Select, { components } from 'react-select';
import { useQuery } from 'react-query'
import clsx from 'clsx';
import { FormGroup, UncontrolledTooltip, CustomInput } from 'reactstrap';

import { useEditorState, useEditorActions } from '../../../editor-context'
import { useProfileAbility } from 'context/profile'
import useOnClickOutside from 'hooks/useOnClickOutside'
import { JurisdictionSelect, useJurisdictionList } from 'components/Shared/Selects/JurisdictionSelect'
import DatePicker from 'components/Shared/DatePicker'
import { LanguageSelect, useLanguageList } from 'components/Shared/Selects/LanguageSelect'
import { DocumentTypesSelect } from 'components/Shared/Selects/DocumentTypesSelect'
import { WorkflowsSelect, useWorkflowList } from 'components/Shared/Selects/WorkflowsSelect'
import { TenantGroupsSelect, useGroupList } from 'components/Shared/Selects/TenantGroupsSelect'
import { SignatureTypesSelect, useSignatureTypeList } from 'components/Shared/Selects/SignatureTypesSelect'
import { BlockchainSelect, useBlockchainList } from 'components/Shared/Selects/BlockchainSelect'
import { NotificationTypesSelect, useNotificationTypesList } from 'components/Shared/Selects/NotificationTypesSelect'
import { ZoomStorageTypesSelect } from 'components/Shared/Selects/ZoomStorageTypeSelect'
import { downloadFile, range } from 'utils'
import { fetcher } from 'api/client'
import { exportContractPackage } from 'api/actions/contracts'


export function General() {
    const { t, i18n } = useTranslation();
    const { document: doc, isEditable, mode: editor } = useEditorState()
    const firstRender = useRef(true);
    const generalContainer = useRef(null);
    const profileAbility = useProfileAbility();
    const { register, control, getValues, setValue, reset, watch, handleSubmit } = useFormContext({
        defaultValues: setValues(doc)
    });
    // useOnClickOutside(generalContainer, (evt) => {
    //     console.log("here")
    //     handleInputChange(evt);
    // })

    const { updateDocument, addParticipant } = useEditorActions()

    useEffect(() => {
        if (doc._id && firstRender.current) {
            reset(setValues(doc))
            firstRender.current = false
        }
    }, [doc])
    
    useEffect(() => {
        reset()
    }, [])

    const expiryUndefined = watch('expiryUndefined', doc.expiryUndefined)
    const expiryEndDate = watch('expiryEndDate', doc.expiryEndDate)
    const expiryStartDate = watch('expiryStartDate', doc.expiryStartDate)
    const jurisdiction = watch('jurisdiction', doc.jurisdiction ?? 'mx')
    const signatureType = watch('signatureType', doc.signatureType  ?? 'none')
    const requireVideoDeclaration = watch('requireVideoDeclaration', doc.requireVideoDeclaration  ?? 'none')
    const isApplication = watch('isApplication', doc.isApplication)

    const isTemplate = editor === 'template';
    // const uploadMethod = 'multiple';
    const uploadMethod = doc.uploadMethod;
    const canViewExternalId = profileAbility.can('VIEW_EXTERNALID'); // can VIEW_EXTERNALID
    const canViewSetWorkflows = profileAbility.can('VIEW_SET_WORKFLOWS'); // can VIEW_SET_WORKFLOWS
    const canLockTemplate = profileAbility.can('LOCK_TEMPLATE'); // can LOCK_TEMPLATE
    const canAddTemplateDescription = profileAbility.can('ADD_TEMPLATE_DESCRIPTION'); // can ADD_TEMPLATE_DESCRIPTION
    const canSetRequireValidation = profileAbility.can('SET_REQUIRE_VALIDATION'); // can SET_REQUIRE_VALIDATION
    const canCreateVoting = profileAbility.can('CREATE_VOTING'); // can CREATE_VOTING
    const canSetStampApproval = profileAbility.can('SET_STAMP_APPROVAL'); // can SET_STAMP_APPROVAL
    const canSetShowNumber= profileAbility.can('SET_SHOW_NUMBER'); // can SET_SHOW_NUMBER
    const canSetNotifyAll= profileAbility.can('SET_NOTIFY_ALL'); // can SET_NOTIFY_ALL
    const canSetNotifyAllSignedToParticipants = profileAbility.can('SET_NOTIFY_ALL_SIGNED_TO_PARTICIPANTS'); // can SET_NOTIFY_ALL_SIGNED_TO_PARTICIPANTS
    const canSetShield3Statement = profileAbility.can('SET_SHIELD3_STATEMENT'); // can SET_SHIELD3_STATEMENT
    const canCreateTestament = profileAbility.can('CREATE_TESTAMENT'); // can CREATE_TESTAMENT
    const canSetRefuseSignature = profileAbility.can('SET_REFUSE_SIGNATURE'); // can CREATE_TESTAMENT
    const canSetVideoDeclaration = profileAbility.can('SET_VIDEO_DECLARATION'); // can SET_VIDEO_DECLARATION
    const canSetPreApprovedTemplate = profileAbility.can('SET_PRE_APPROVED_TEMPLATE'); // can SET_PRE_APPROVED_TEMPLATE
    const canSetMedicalPrescription = profileAbility.can('SET_MEDICAL_PRESCRIPTION'); // can SET_MEDICAL_PRESCRIPTION

    function setValues(doc) {
        return {
            ...doc,
            jurisdiction: doc.jurisdiction ?? 'mx',
            language:doc.language?.toLowerCase?.() ?? 'es',
            type: doc.type || null,
            // signatureType: signatureTypesMap.get(doc.signatureType),
            // group: groupsMap.get(doc.group),
            blockchain: doc.blockchain?.type ?? 'none',
            notificationType: doc.notificationType ?? 'email'
        }
    }

    const handleSuggestPwd = () => {
        const today =  format(new Date(), 'yyyyMMdd'); 
        const pwd = pwdgen.generate({ length: 4, numbers: false, uppercase: true });
        const password = `${today}-${pwd}`;
        setValue('password', password.toUpperCase());
        handleInputChange()
    }

    const handleDownload = async () => {
        const data = await fetcher(exportContractPackage(doc._id));

        downloadFile({
            name: `${doc.documentId}_${doc.name}`,
            blob: new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            ext: 'xlsx'
        })();
    }

    const handleExpiryUndefined = () => {
        setValue('expiryEndDate', null);
    }

    const handleSelectChange = (name, option = {}) => {
        console.log(`${name} change`)
       
  
        handleSubmit((data) => {
            updateDocument(data)
        }, (err) => console.log({err}))()
    }

    const handleInputChange = (evt) => {
        handleSubmit((data) => {
            updateDocument(data)
        }, (err) => console.log({err}))()
    }
    
    const handleTestamentParticipants = (isTestament) => {
        if (isTestament) {
            updateDocument({ participants: [] })
            addParticipant({ 
                label: 'TESTADOR',
                testamentRole: 'testator'
            })
            addParticipant({
                label: 'LEGATARIO',
                testamentRole: 'legatee'
            })
            addParticipant({
                label: 'ALBACEA',
                testamentRole: 'executor'
            })
            range(1, 5).map((n) => {
                addParticipant({ 
                    label: `TESTIGO ${n}`,
                    testamentRole: 'witness',
                })
            })
            
        } 
    }

    const handleDateChange = (date) => {
        console.log(`${date} change`)
        handleSubmit((data) => updateDocument(data), (err) => console.log({err}))()
    }

    const handleKeyPress = (evt) => {
        if(evt.key === 'Enter'){
            handleInputChange(evt);
        }
      }

    return (
        <div className="p-2" ref={generalContainer}>
            <FormGroup>
                <Label required name="document_name">{t('editor.config.document_name')}</Label>
                <div className="d-flex">
                    {!isTemplate && 
                        <input 
                            type="text" 
                            className="form-control input-sm mr-2" 
                            name="documentId" 
                            placeholder="" 
                            ref={register}
                            defaultValue={doc.documentId}
                            readOnly 
                            style={{width: 60, marginLeft: 4}} 
                        />
                    }
                    <input 
                        type="text" 
                        className="form-control input-sm"
                        name="name" 
                        onBlur={handleInputChange}
                        placeholder={t('editor.config.document_name_placeholder')}
                        ref={register}
                        defaultValue={doc.name || ''}
                        onKeyPress={handleKeyPress}
                        disabled={!isEditable && !isTemplate}
                        readOnly={!isEditable && !isTemplate}
                        style={{flex:1}}
                    />
                </div>
            </FormGroup>
            <SubSection show={canViewExternalId && editor === 'contract'}>
                <FormGroup>
                    <Label help={t('editor.config.externalId_help')} name="externalId">
                        {t('editor.config.externalId')}
                    </Label>
                    <input 
                        name="externalId" 
                        className="form-control" 
                        ref={register}
                        onBlur={handleInputChange}
                        onKeyPress={handleKeyPress}
                        defaultValue={doc.externalId || ''}
                        disabled={!isEditable && !isTemplate}
                        readOnly={!isEditable && !isTemplate}
                    />
                </FormGroup>
            </SubSection>
            <FormGroup>
                <Label help={t('editor.config.jurisdiction')} name="jurisdiction">
                    {t('editor.config.jurisdiction')}
                </Label>
                <Controller
                    control={control}
                    name="jurisdiction" 
                    defaultValue={doc.jurisdiction ?? 'mx'}
                    render={({ name, onChange, onBlur, value}) => (
                        <JurisdictionSelect
                            onChange={(option) => {
                                onChange(option)
                                handleSelectChange(name, option)
                            }}
                            value={value}
                            onBlur={onBlur}
                            isDisabled={!isEditable && !isTemplate}
                        />
                    )}
                />
            </FormGroup>
            <FormGroup>
                <Label help={t('editor.config.language_help')} name="language">
                    {t('editor.config.language')}
                </Label>
                <Controller
                    control={control}
                    name="language" 
                    defaultValue={doc.language?.toLowerCase?.() ?? 'es'}
                    render={({ name, onChange, onBlur, value}) => (
                        <LanguageSelect
                            onChange={(value, option) => {
                                onChange(value, option)
                                handleSelectChange(name, option)
                            }}
                            onBlur={onBlur}
                            value={value}
                            isDisabled={!isEditable && !isTemplate}
                        />
                    )}
                />
            </FormGroup>
            <FormGroup>
                <Label required name="document_type">
                    {t('editor.config.document_type')}
                </Label>
                <Controller
                    control={control}
                    name="type" 
                    rules={{ required: true }}
                    defaultValue={doc.type || null}
                    render={({ name, onChange, onBlur, value}) => (
                        <DocumentTypesSelect
                            onChange={(value, option) => {
                                onChange(value)
                                handleSelectChange(name, option)
                            }}
                            onBlur={onBlur}
                            value={value}
                            isDisabled={!isEditable && !isTemplate}
                        />
                    )}
                />
            </FormGroup>
            <SubSection show>
                <FormGroup>
                    <Label name="workflow">{t('contracts.new.workflow_title')}</Label>
                    <Controller
                        control={control}
                        name="workflow"
                        defaultValue={doc.workflow || null}
                        render={({ name, onChange, onBlur, value}) => (
                            <WorkflowsSelect
                                onChange={(value, option) => {
                                    onChange(value)
                                    handleSelectChange(name, option)
                                }}
                                onBlur={onBlur}
                                value={value}
                                placeholder={t('workflow.choose')}
                                title={!canViewSetWorkflows ?  t('wordList.plans.availableE') : ''}
                                isDisabled={!canViewSetWorkflows || (!isEditable && !isTemplate)}
                            />
                        )}
                    />
                </FormGroup>
            </SubSection>
            <FormGroup>
                <Label name="group">{t('reports.group')}</Label>
                <Controller
                    control={control}
                    name="group" 
                    defaultValue={doc.group || null}
                    render={({ name, onChange, onBlur, value}) => (
                        <TenantGroupsSelect
                            onChange={(value, option) => {
                                onChange(value)
                                handleSelectChange(name, option)
                            }}
                            placeholder={t('chooseGroup')}
                            onBlur={onBlur}
                            value={value}
                            isDisabled={!isEditable && !isTemplate}
                            isClearable
                        />
                    )}
                />
            </FormGroup>
            <SubSection 
                show={editor === 'template'}
            >
                <Controller
                    control={control}
                    name="locked" 
                    defaultValue={doc.locked || false}
                    render={({ onChange, onBlur, value }) => (
                        <Checkbox 
                            name="locked" 
                            label={t('editor.config.locked')}
                            tooltip={!canLockTemplate ? t('wordList.plans.availableE') : ''}
                            disabled={!isEditable || !canLockTemplate}
                            onBlur={onBlur}
                            checked={value}
                            onChange={(evt) => {
                                onChange(evt.target.checked)
                                handleInputChange(evt)
                            }}
                        />
                    )}
                />
            </SubSection> 
            <Section 
                name="description"
                title={t('editor.config.description')}
                show={canAddTemplateDescription &&  editor === 'template'}
            >
                <div>
                    <div>
                        <textarea 
                            required 
                            name="description"
                            ref={register} 
                            onBlur={handleInputChange}
                            onKeyPress={handleKeyPress}
                            defaultValue={doc.description}
                            className="form-control input-sm"
                            disabled={!isEditable}
                            readOnly={!isEditable}
                        />
                    </div>
                    <Controller
                        control={control}
                        name="clientShared" 
                        defaultValue={doc.clientShared || false}
                        render={({ onChange, onBlur, value }) => (
                            <Checkbox 
                                name="clientShared" 
                                label={t('editor.config.clientShared')}
                                disabled={!isEditable}
                                onBlur={onBlur}
                                checked={value}
                                onChange={(evt) => {
                                    onChange(evt.target.checked)
                                    handleInputChange(evt)
                                }}
                            />
                        )}
                    />
                </div>
            </Section>

            <Section 
                name="flow_control"
                className="flow_control"
                title={t('editor.config.flow_control')}
                show={signatureType !== 'fad'}
            >
                {/* <Controller
                    control={control}
                    name="validationRequired" 
                    defaultValue={doc.validationRequired || false}
                    render={({ onChange, onBlur, value}) => (
                        <Checkbox
                            name="validationRequired" 
                            label={t('editor.config.validation')}
                            tooltip={!canSetRequireValidation ? t('wordList.plans.availablePyPE') : ''}
                            disabled={!isEditable || !canSetRequireValidation}
                            onBlur={onBlur}
                            checked={value}
                            onChange={(evt) => {
                                onChange(evt.target.checked)
                                handleInputChange(evt)
                            }}
                        />
                    )}
                />  */}
                <Controller
                    control={control}
                    name="shield3ValidationRequired" 
                    defaultValue={doc.shield3ValidationRequired || true}
                    render={({ onChange, onBlur, value}) => (
                        <Checkbox 
                            name="shield3ValidationRequired" 
                            label={t('editor.config.validateShield3')}
                            disabled={!isEditable}
                            onBlur={onBlur}
                            checked={value}
                            onChange={(evt) => {
                                onChange(evt.target.checked)
                                handleInputChange(evt)
                            }}
                        />
                    )}
                /> 
                <SubSection show={canCreateVoting}>
                    <Controller
                        control={control}
                        name="isVotable" 
                        defaultValue={doc.isVotable ?? false}
                        render={({ onChange, onBlur, value}) => (
                            <Checkbox 
                                name="isVotable" 
                                label={t('editor.config.isVotable')}
                                disabled={!isEditable}
                                onBlur={onBlur}
                                checked={value}
                                onChange={(evt) => {
                                    onChange(evt.target.checked)
                                    handleInputChange(evt)
                                }}
                            />
                        )}
                    />                 
                </SubSection>
                <SubSection show={canCreateTestament && editor === 'template'}>
                    <Controller
                        control={control}
                        name="isTestament" 
                        defaultValue={doc.isTestament ?? false}
                        render={({ onChange, onBlur, value}) => (
                            <Checkbox 
                                name="isTestament" 
                                label={t('Establecer como testamento')}
                                disabled={!isEditable}
                                onBlur={onBlur}
                                checked={value}
                                onChange={(evt) => {
                                    onChange(evt.target.checked)
                                    handleTestamentParticipants(evt.target.checked)
                                    handleInputChange(evt)
                                }}
                            />
                        )}
                    />                 
                </SubSection>
                <SubSection show={['certificate', 'autograph'].includes(signatureType)}>  
                    <Controller
                        control={control}
                        name="isPagare" 
                        defaultValue={doc.isPagare ?? false}
                        render={({ onChange, onBlur, value}) => (
                            <Checkbox 
                                name="isPagare" 
                                label={t('editor.config.isPagare')}
                                disabled={!isEditable}
                                onBlur={onBlur}
                                checked={value}
                                onChange={(evt) => {
                                    onChange(evt.target.checked)
                                    handleInputChange(evt)
                                }}
                            />
                        )}
                    />               
                </SubSection>
                <SubSection show={canSetMedicalPrescription && ['autograph'].includes(signatureType)}> 
                    <Controller
                        control={control}
                        name="isMedicalPrescription" 
                        defaultValue={doc.isMedicalPrescription ?? false}
                        render={({ onChange, onBlur, value}) => (
                            <Checkbox 
                                name="isMedicalPrescription" 
                                label={t('Es receta médica')}
                                disabled={!isEditable}
                                onBlur={onBlur}
                                checked={value}
                                onChange={(evt) => {
                                    onChange(evt.target.checked)
                                    handleInputChange(evt)
                                }}
                            />
                        )}
                    />               
                </SubSection>
                <SubSection show={editor === 'template'}>
                    <SubSection show={canSetPreApprovedTemplate}>
                        <Controller
                            control={control}
                            name="isPreApproved" 
                            defaultValue={doc.isPreApproved || false}
                            render={({ onChange, onBlur, value}) => (
                                <Checkbox 
                                    name="isPreApproved" 
                                    label={t('editor.config.pre_approved')}
                                    onBlur={onBlur}
                                    checked={value}
                                    onChange={(evt) => {
                                        onChange(evt.target.checked)
                                        handleInputChange(evt)
                                    }}
                                />
                            )}
                        />
                    </SubSection>  
                    <Controller
                        control={control}
                        name="isApplication" 
                        defaultValue={doc.isApplication || false}
                        render={({ onChange, onBlur, value }) => (
                            <Checkbox 
                                name="isApplication" 
                                label={t('editor.config.isApplication')}
                                disabled={!isEditable}
                                onBlur={onBlur}
                                checked={value}
                                onChange={(evt) => {
                                    onChange(evt.target.checked)
                                    handleInputChange(evt)
                                }}
                            />
                        )}
                    />  
                     <SubSection show={isApplication}>
                        <Controller
                            control={control}
                            name="validateRequiredFields" 
                            defaultValue={doc.validateRequiredFields || false}
                            render={({ onChange, onBlur, value}) => (
                                <Checkbox 
                                    name="validateRequiredFields" 
                                    label={t('editor.config.validateRequiredFields')}
                                    onBlur={onBlur}
                                    checked={value}
                                    onChange={(evt) => {
                                        onChange(evt.target.checked)
                                        handleInputChange(evt)
                                    }}
                                />
                            )}
                        />
                    </SubSection> 
                </SubSection>
                <SubSection show={canSetRefuseSignature}>
                <Controller
                        control={control}
                        name="allowParticipantsToRefuseSignature" 
                        defaultValue={doc.allowParticipantsToRefuseSignature || false}
                        render={({ onChange, onBlur, value }) => (
                            <Checkbox 
                                name="allowParticipantsToRefuseSignature" 
                                label={t('editor.config.allowParticipantsToRefuseSignature')}
                                disabled={!isEditable}
                                onBlur={onBlur}
                                checked={value}
                                onChange={(evt) => {
                                    onChange(evt.target.checked)
                                    handleInputChange(evt)
                                }}
                            />
                        )}
                    /> 
                </SubSection>
                <SubSection show={canSetVideoDeclaration}>
                    <Controller
                        control={control}
                        name="requireVideoDeclaration" 
                        defaultValue={doc.requireVideoDeclaration || false}
                        render={({ onChange, onBlur, value }) => (
                            <Checkbox 
                                name="requireVideoDeclaration" 
                                label={t('editor.config.requireVideoDeclaration')}
                                disabled={!isEditable}
                                onBlur={onBlur}
                                checked={value}
                                onChange={(evt) => {
                                    onChange(evt.target.checked)
                                    handleInputChange(evt)
                                }}
                            />
                        )}
                    /> 
                    <Section
                        className="mt-1"
                        name="zoomStorageType"
                        title={t('Guardar las grabaciones en')}
                        help={t('Selecciona el tipo de guardado que se usara para las llamadas de Zoom')}
                        show={requireVideoDeclaration}
                    > 
                        <Controller
                            control={control}
                            name="stampZoomVideo" 
                            defaultValue={doc.requireVideoDeclaration || false}
                            render={({ onChange, onBlur, value }) => (
                                <Checkbox 
                                    name="stampZoomVideo" 
                                    label={t('Estampar el video de la llamada en el documento')}
                                    disabled={!isEditable}
                                    onBlur={onBlur}
                                    checked={value}
                                    onChange={(evt) => {
                                        onChange(evt.target.checked)
                                        handleInputChange(evt)
                                    }}
                                />
                            )}
                        /> 
                        <Controller
                            control={control}
                            name="zoomStorageType" 
                            defaultValue={doc.zoomStorageType ?? 'local'}
                            render={({ name, onChange, onBlur, value}) => (
                                <ZoomStorageTypesSelect
                                    onChange={(value, option) => {
                                        onChange(value)
                                        handleSelectChange(name, option)
                                    }}
                                    value={value}
                                    onBlur={onBlur}
                                    isDisabled={!isEditable}
                                />
                            )}
                        />
                    </Section>
                </SubSection>
            </Section>
            <Section
                title={t('editor.config.export_pdf')}
                tooltip={!canSetStampApproval || !canSetShowNumber ? t('wordList.plans.availableE') : ''} 
                show={signatureType !== 'fad'} 
            >
                <Controller
                    control={control}
                    name="stampApproval" 
                    defaultValue={doc.stampApproval || false}
                    render={({ onChange, onBlur, value }) => (
                        <Checkbox 
                            name="stampApproval" 
                            label={t('editor.config.stampApproval')}
                            disabled={!isEditable}
                            onBlur={onBlur}
                            checked={value}
                            onChange={(evt) => {
                                onChange(evt.target.checked)
                                handleInputChange(evt)
                            }}
                        />
                    )}
                /> 
                <Controller
                    control={control}
                    name="showPageNumber" 
                    defaultValue={doc.showPageNumber || false}
                    render={({ onChange, onBlur, value }) => (
                        <Checkbox 
                            name="showPageNumber" 
                            label={t('editor.config.showPageNumber')}
                            disabled={!isEditable}
                            onBlur={onBlur}
                            checked={value}
                            onChange={(evt) => {
                                onChange(evt.target.checked)
                                handleInputChange(evt)
                            }}
                        />
                    )}
                /> 
                <Controller
                    control={control}
                    name="showDocumentId" 
                    defaultValue={doc.showDocumentId || false}
                    render={({ onChange, onBlur, value }) => (
                        <Checkbox 
                            name="showDocumentId" 
                            label={t('editor.config.showDocumentId')}
                            disabled={!isEditable}
                            onBlur={onBlur}
                            checked={value}
                            onChange={(evt) => {
                                onChange(evt.target.checked)
                                handleInputChange(evt)
                            }}
                        />
                    )}
                /> 
            </Section>
            <Section name="other_settings" title={t('editor.config.other_settings')} show>
                <Controller
                    control={control}
                    name="allowAttachments" 
                    defaultValue={doc.allowAttachments || false}
                    render={({ onChange, onBlur, value }) => (
                        <Checkbox 
                            name="allowAttachments" 
                            label={t('editor.config.additional_attachments')}
                            disabled={!isEditable}
                            onBlur={onBlur}
                            checked={value}
                            onChange={(evt) => {
                                onChange(evt.target.checked)
                                handleInputChange(evt)
                            }}
                        />
                    )}
                />
                <SubSection show={!['fad', 'none'].includes(signatureType)}>
                    <SubSection show={jurisdiction  === 'mx'}>
                        <Controller
                            control={control}
                            name="stampNom" 
                            defaultValue={doc.stampNom || false}
                            render={({ onChange, onBlur, value }) => (
                                <Checkbox 
                                    name="stampNom" 
                                    label={t('editor.config.nom')}
                                    disabled={!isEditable}
                                    onBlur={onBlur}
                                    checked={value}
                                    onChange={(evt) => {
                                        onChange(evt.target.checked)
                                        handleInputChange(evt)
                                    }}
                                />
                            )}
                        />
                    </SubSection>
                    <SubSection show={['pr', 'co'].includes(jurisdiction)}>
                        <Controller
                            control={control}
                            name="stamprfc3161" 
                            defaultValue={doc.stamprfc3161 || false}
                            render={({ onChange, onBlur, value }) => (
                                <Checkbox 
                                    name="stamprfc3161" 
                                    label={t('editor.config.stamprfc3161', { context: doc.jurisdiction })}
                                    disabled={!isEditable}
                                    onBlur={onBlur}
                                    checked={value}
                                    onChange={(evt) => {
                                        onChange(evt.target.checked)
                                        handleInputChange(evt)
                                    }}
                                />
                            )}
                        />
                    </SubSection>
                </SubSection>
            </Section>
            <Section 
                name="notification"
                className="mb-1"
                title={t('editor.config.notification')} 
                help={t('editor.config.notification_help')} 
                style={{marginBottom: 0}}
                show={signatureType !== 'fad'}
            >
                <Controller
                    control={control}
                    name="notificationType" 
                    defaultValue={doc.notificationType ?? 'email'}
                    render={({ name, onChange, onBlur, value}) => (
                        <NotificationTypesSelect
                            onChange={(value, option) => {
                                onChange(value)
                                handleSelectChange(name, option)
                            }}
                            value={value}
                            onBlur={onBlur}
                            isDisabled={!isEditable}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="notifyall" 
                    defaultValue={doc.notifyall || false}
                    render={({ onChange, onBlur, value }) => (
                        <Checkbox 
                            name="notifyall" 
                            className=""
                            label={t('editor.config.notifyall')}
                            tooltip={!canSetNotifyAll ? t('wordList.plans.availablePE') : ''}
                            disabled={!isEditable || !canSetNotifyAll}
                            onBlur={onBlur}
                            checked={value}
                            onChange={(evt) => {
                                onChange(evt.target.checked)
                                handleInputChange(evt)
                            }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="notifyAllSignedToParticipants" 
                    defaultValue={doc.notifyAllSignedToParticipants || false}
                    render={({ onChange, onBlur, value }) => (
                        <Checkbox 
                            name="notifyAllSignedToParticipants" 
                            label={t('editor.config.notifyAllSignedToParticipants')}
                            tooltip={!canSetNotifyAllSignedToParticipants ? t('wordList.plans.availablePE') : ''}
                            disabled={!isEditable || !canSetNotifyAllSignedToParticipants}
                            onBlur={onBlur}
                            checked={value}
                            onChange={(evt) => {
                                onChange(evt.target.checked)
                                handleInputChange(evt)
                            }}
                        />
                    )}
                />
            </Section>
            <Section
                name="signature"
                title={t('editor.config.signature')}
                help={t('editor.config.signature_help')}
                show
            >
                <Controller
                    control={control}
                    name="signatureType"
                    defaultValue={doc.signatureType ?? 'none'} 
                    render={({ name, onChange, onBlur, value}) => (
                        <SignatureTypesSelect
                            onChange={(value, option) => {
                                onChange(value)
                                handleSelectChange(name, option)
                            }}
                            onBlur={onBlur}
                            value={value}
                            isDisabled={!isEditable}
                            isPdf={false}
                        />
                    )}
                />
            </Section>
            <Section
                name="blockchain"
                title="Blockchain"
                help={t('editor.config.blockchain_help')}
                show={signatureType !== 'none' && signatureType !== 'fad'}
            >
                <Controller
                    control={control}
                    name="blockchain" 
                    defaultValue={doc.blockchain?.type ?? 'none'}
                    render={({ name, onChange, onBlur, value}) => (
                        <BlockchainSelect
                            onChange={(value, option) => {
                                onChange(value)
                                handleSelectChange(name, option)
                            }}
                            onBlur={onBlur}
                            value={value}
                            isDisabled={!isEditable}
                        />
                    )}
                />
            </Section>
            <Section
                name="password"
                title={t('editor.config.password')}
                help={t('editor.config.password_help')}
                show={editor === 'contract' && signatureType !== 'fad'}
            >
                <div className="d-flex">
                    <input 
                        type="text" 
                        data-lpignore="true" 
                        className="form-control input-sm" 
                        name="password" 
                        ref={register}
                        placeholder={t('editor.config.password_placeholder')} 
                        style={{flex: 1}} 
                        onBlur={handleInputChange}
                        onKeyPress={handleKeyPress}
                        disabled={!isEditable}
                        readOnly={!isEditable} 
                        autoComplete="nope"
                    />
                    <button 
                        className="btn btn-sm btn-primary btn-block" 
                        style={{width: 80, marginLeft: 4}} 
                        data-action="suggest-pwd" 
                        onClick={handleSuggestPwd}
                        disabled={!isEditable}
                        readOnly={!isEditable} 
                    >
                    <i className="fas fa-key"></i> {t('editor.config.password_generate')}
                    </button>
                </div>
            </Section>
            <Section
                name="shield3_statement"
                title={t('editor.config.shield3_statement')}
                show={canSetShield3Statement}
            >
                <textarea 
                    required 
                    name="shield3Statement" 
                    className="form-control input-sm" 
                    title={t('editor.config.shield3_statement_help')}
                    ref={register}
                    onBlur={handleInputChange}
                    onKeyPress={handleKeyPress}
                    disabled={!isEditable}
                    readOnly={!isEditable} 
                />
            </Section>
            <Section
                name="expiry"
                title={t('editor.config.expiry')}
                help={t('editor.config.expiry_help')}
                show={editor === 'contract'}
                TitleInput={
                    <Controller
                        control={control}
                        name="expiryUndefined" 
                        defaultValue={doc.expiryUndefined || false}
                        render={({ onChange, onBlur, value }) => (
                            <Checkbox 
                                name="expiryUndefined" 
                                label={t('editor.config.expiry_undefined')}
                                disabled={!isEditable}
                                onBlur={onBlur}
                                checked={value}
                                style={{margin: 0}} 
                                onChange={(evt) => {
                                    onChange(evt.target.checked)
                                    handleExpiryUndefined()
                                    handleInputChange(evt)
                                }}
                            />
                        )}
                    />
                }
                labelClassName={'block'}

            >
                <div className="d-flex">
                    <Controller
                        control={control}
                        name="expiryStartDate" 
                        defaultValue={doc.expiryStartDate || null}
                        render={({ onChange, onBlur, value}) => (
                            <DatePicker
                                locale={i18n.language}
                                selected={value ? new Date(value) : null}
                                onChange={(date) => {
                                    onChange(date)
                                    handleDateChange(date)
                                }}
                                maxDate={expiryEndDate ? new Date(expiryEndDate) : null}
                                onBlur={onBlur}
                                disabled={!isEditable}
                                customInput={<input className="form-control input-sm" type="text" />}
                                dateFormat="dd/MMM/yyyy"
                                placeholderText={t('editor.config.expiry_start_placeholder')} 
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="expiryEndDate" 
                        defaultValue={doc.expiryEndDate || null}
                        render={({ onChange, onBlur, value}) => (
                            <DatePicker
                                locale={i18n.language}
                                className="ml-2"
                                selected={value ? new Date(value) : null}
                                onChange={(date) => {
                                    onChange(date)
                                    handleDateChange(date)
                                }}
                                minDate={expiryStartDate ? new Date(expiryStartDate) : null}
                                onBlur={onBlur}
                                disabled={!isEditable || expiryUndefined}
                                customInput={<input className="form-control input-sm" type="text" />}
                                dateFormat="dd/MMM/yyyy"
                                placeholderText={t('editor.config.expiry_end_placeholder')} 
                            />
                        )}
                    />
                </div>
            </Section>
            <Section
                name="uploadMethod"
                show={editor === 'contract'}
            >
                <button className="btn btn-sm btn-success btn-block" data-action="download-contract" onClick={handleDownload}>
                    <i className="fas fa-download"></i> {t('editor.config.export')}
                </button>
            </Section>
    </div> 
    )
}

function Input({ register, disabled, ...props}) {
    const  { t } = useTranslation();

    return (
        <input />
    )
}


function Label({ className, help, required, children, name }) {
    return (
        <label className={clsx('text-dark', className)}> 
            {children} {required && '*'}
            { help &&
                    <i className="fas fa-question-circle text-dark" id={name}></i>
                    
            }
            {name && help && <UncontrolledTooltip target={name}>{help}</UncontrolledTooltip>}
        </label>
    )
}

function Section ({ className, title, tooltip, help, show, TitleInput, children, name }) {
    if (!show) {
        return null;
    }

   return ( 
        <FormGroup className={clsx(className)} title={tooltip}>
            <div className="d-flex justify-content-between text-dark">
                <div>
                    { title && 
                        <Label className="font-weight-bold" help={help} name={name}>{title}</Label> 
                    }
                </div>
                { TitleInput &&  
                    <div>
                        {React.cloneElement(TitleInput, {})}
                    </div> 
                }
            </div>
            <div>{children}</div>
        </FormGroup>
    )
}

function SubSection ({ show, children }) {
    if (!show) {
        return null;
    }

   return ( 
        <>
            {children}
        </>
    )
}

export function Checkbox({ className = 'm-t-none', label, labelClassName = '', disabled, onChange, onBlur, checked, tooltip, style, name }) {
    return (
        <CustomInput 
            className={clsx('mt-1 text-dark',className)}
            type="switch" 
            onBlur={onBlur}
            checked={checked}
            onChange={onChange}
            id={name}
            disabled={disabled}
            readOnly={disabled}
            label={label}
        />
            
    )
}
